import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  Snackbar,
  alpha,
  Chip,
  IconButton,
  Tooltip,
  Skeleton,
  Dialog,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import * as pcpService from "../services/pcpAssignmentService";
import AssignmentSummary from "../components/AssignmentSummary";
import PcpPanel from "../components/PcpPanel";
import TransferDialog from "../components/TransferDialog";
import PatientHistoryDialog from "../components/PatientHistoryDialog";
import AssignPcpDialog from "../components/AssignPcpDialog";
import PcpConfigDialog from "../components/PcpConfigDialog";

// ── Status chip para assignmentSource ──────────────────────────────
const SOURCE_CONFIG = {
  MANUAL: { label: "Manual", bg: "#DBEAFE", color: "#1E40AF" },
  AUTO_HIGHRISK: { label: "High Risk", bg: "#FEE2E2", color: "#991B1B" },
  AUTO_HIGHCOST: { label: "High Cost", bg: "#FEF3C7", color: "#92400E" },
  AUTO_MEDICARE: { label: "Medicare", bg: "#E0E7FF", color: "#3730A3" },
  AUTO_AGE: { label: "Age Match", bg: "#D1FAE5", color: "#065F46" },
  AUTO_CRITICAL: { label: "Critical", bg: "#FCE7F3", color: "#9D174D" },
  AUTO_REMAINING: { label: "Auto", bg: "#F3F4F6", color: "#374151" },
};

/**
 * Página principal del módulo de Asignación PCP.
 * Usa endpoints reales de /api/pcp/* para gestionar asignaciones.
 */
export default function AssignmentsPage() {
  // ── State ────────────────────────────────────────────────────────
  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  const [pcps, setPcps] = useState([]);
  const [loadingPcps, setLoadingPcps] = useState(false);

  const [unassigned, setUnassigned] = useState([]);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  const [assignedPatients, setAssignedPatients] = useState([]);
  const [loadingAssigned, setLoadingAssigned] = useState(false);
  const [selectedPcp, setSelectedPcp] = useState(null);

  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Dialogs
  const [assignDialog, setAssignDialog] = useState({ open: false, patient: null });
  const [transferDialog, setTransferDialog] = useState({ open: false, pcp: null });
  const [historyDialog, setHistoryDialog] = useState({ open: false, cipId: null, name: "" });
  const [configDialog, setConfigDialog] = useState({ open: false, pcp: null });
  const [autoAssignConfirm, setAutoAssignConfirm] = useState(false);
  const [autoAssigning, setAutoAssigning] = useState(false);

  // ── Loaders ──────────────────────────────────────────────────────
  const loadSummary = useCallback(async () => {
    setLoadingSummary(true);
    try {
      const data = await pcpService.getAssignmentSummary();
      setSummary(data || []);
      if (!selectedLocationId && data?.length > 0) {
        setSelectedLocationId(data[0].locationId);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSummary(false);
    }
  }, [selectedLocationId]);

  const loadLocationData = useCallback(async (locationId) => {
    if (!locationId) return;
    setLoadingPcps(true);
    setLoadingUnassigned(true);
    setSelectedPcp(null);
    setAssignedPatients([]);
    try {
      const [pcpData, unassignedData] = await Promise.all([
        pcpService.getPcpsByLocation(locationId),
        pcpService.getUnassignedPatients(locationId),
      ]);
      setPcps(pcpData || []);
      setUnassigned(unassignedData || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingPcps(false);
      setLoadingUnassigned(false);
    }
  }, []);

  const loadAssignedPatients = useCallback(async (pcp) => {
    if (!pcp || !selectedLocationId) return;
    setLoadingAssigned(true);
    try {
      const data = await pcpService.getAssignedPatients(pcp.employeePcpId, selectedLocationId);
      setAssignedPatients(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingAssigned(false);
    }
  }, [selectedLocationId]);

  // ── Effects ──────────────────────────────────────────────────────
  useEffect(() => { loadSummary(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (selectedLocationId) loadLocationData(selectedLocationId); }, [selectedLocationId, loadLocationData]);

  // ── Refresh helper ──────────────────────────────────────────────
  const refreshAll = useCallback(async () => {
    await loadSummary();
    if (selectedLocationId) await loadLocationData(selectedLocationId);
    if (selectedPcp) await loadAssignedPatients(selectedPcp);
  }, [loadSummary, selectedLocationId, loadLocationData, selectedPcp, loadAssignedPatients]);

  // ── Handlers ─────────────────────────────────────────────────────
  const handleSelectLocation = (loc) => setSelectedLocationId(loc.locationId);

  const handleViewPcp = (pcp) => {
    setSelectedPcp(pcp);
    loadAssignedPatients(pcp);
  };

  const handleAssignPatient = async (patient, pcp) => {
    try {
      const res = await pcpService.assignPatient({
        cipId: patient.cipId,
        employeePcpId: pcp.employeePcpId,
        locationId: selectedLocationId,
      });
      setSnackbar({ open: true, message: res.message, severity: "success" });
      setAssignDialog({ open: false, patient: null });
      await refreshAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleUnassignPatient = async (cipId) => {
    try {
      const res = await pcpService.unassignPatient(cipId);
      setSnackbar({ open: true, message: res.message, severity: "success" });
      await refreshAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    }
  };

  const handleTransfer = async (transferData) => {
    try {
      const res = await pcpService.transferPatients(transferData);
      setSnackbar({ open: true, message: res.message, severity: "success" });
      await refreshAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
      throw err;
    }
  };

  const handleAutoAssign = async () => {
    setAutoAssigning(true);
    try {
      const res = await pcpService.autoAssign();
      setSnackbar({ open: true, message: res.message, severity: "success" });
      setAutoAssignConfirm(false);
      await refreshAll();
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: "error" });
    } finally {
      setAutoAssigning(false);
    }
  };

  // ── Columns for Unassigned patients ─────────────────────────────
  const unassignedColumns = [
    { field: "memberFullName", headerName: "Patient", flex: 1, minWidth: 200, renderCell: ({ value }) => <Typography variant="body2" fontWeight={600}>{value}</Typography> },
    { field: "cipId", headerName: "CIP ID", width: 130 },
    { field: "age", headerName: "Age", width: 70, align: "center", headerAlign: "center" },
    { field: "gender", headerName: "Gender", width: 80, align: "center", headerAlign: "center" },
    {
      field: "actions", headerName: "", width: 120, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Assign to PCP">
            <IconButton size="small" color="primary" onClick={() => setAssignDialog({ open: true, patient: row })}>
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton size="small" onClick={() => setHistoryDialog({ open: true, cipId: row.cipId, name: row.memberFullName })}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  // ── Columns for Assigned patients ───────────────────────────────
  const assignedColumns = [
    { field: "memberFullName", headerName: "Patient", flex: 1, minWidth: 200, renderCell: ({ value }) => <Typography variant="body2" fontWeight={600}>{value}</Typography> },
    { field: "cipId", headerName: "CIP ID", width: 130 },
    { field: "age", headerName: "Age", width: 70, align: "center", headerAlign: "center" },
    { field: "gender", headerName: "Gender", width: 80, align: "center", headerAlign: "center" },
    {
      field: "assignmentSource", headerName: "Source", width: 130,
      renderCell: ({ value }) => {
        const cfg = SOURCE_CONFIG[value] || SOURCE_CONFIG.MANUAL;
        return <Chip label={cfg.label} size="small" sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600, bgcolor: cfg.bg, color: cfg.color }} />;
      },
    },
    {
      field: "crossLocation", headerName: "Cross-Loc", width: 90, align: "center", headerAlign: "center",
      renderCell: ({ value }) => value ? <WarningAmberIcon sx={{ fontSize: 18, color: "#F59E0B" }} /> : null,
    },
    {
      field: "actions", headerName: "", width: 120, sortable: false, filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Unassign">
            <IconButton size="small" color="error" onClick={() => handleUnassignPatient(row.cipId)}>
              <PersonRemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="View History">
            <IconButton size="small" onClick={() => setHistoryDialog({ open: true, cipId: row.cipId, name: row.memberFullName })}>
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1400, mx: "auto" }}>
      <Stack spacing={3}>
        {/* ── Header ──────────────────────────────────────────── */}
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }} spacing={2}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 44, height: 44, borderRadius: 3,
              background: "linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)",
              color: "white", boxShadow: "0 4px 14px rgba(99, 102, 241, 0.4)",
            }}>
              <AssignmentIndIcon />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={800}>PCP Assignment</Typography>
              <Typography variant="body2" color="text.secondary">Manage patient-to-PCP assignments across locations</Typography>
            </Box>
          </Stack>
          <Button
            startIcon={<AutoFixHighIcon />}
            variant="contained"
            onClick={() => setAutoAssignConfirm(true)}
            sx={{
              borderRadius: 2, textTransform: "none", fontWeight: 600,
              background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)",
              boxShadow: "0 4px 12px rgba(139, 92, 246, 0.3)",
            }}
          >
            Auto-Assign
          </Button>
        </Stack>

        {error && <Alert severity="error" onClose={() => setError(null)} sx={{ borderRadius: 3 }}>{error}</Alert>}

        {/* ── Summary Cards ────────────────────────────────────── */}
        <AssignmentSummary
          summary={summary}
          loading={loadingSummary}
          selectedLocationId={selectedLocationId}
          onSelectLocation={handleSelectLocation}
        />

        {/* ── Main Content: PCPs + Patients ────────────────────── */}
        {selectedLocationId && (
          <Stack direction={{ xs: "column", lg: "row" }} spacing={2.5}>
            {/* Left: PCP Panel */}
            <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider", width: { lg: 360 }, flexShrink: 0 }}>
              <PcpPanel
                pcps={pcps}
                loading={loadingPcps}
                selectedPcpId={selectedPcp?.employeePcpId}
                onViewPcp={handleViewPcp}
                onTransferPcp={(pcp) => setTransferDialog({ open: true, pcp })}
                onConfigurePcp={(pcp) => setConfigDialog({ open: true, pcp })}
              />
            </Paper>

            {/* Right: Patient Tables */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack spacing={2.5}>
                {/* Unassigned Patients */}
                <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                  <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: (theme) => alpha(theme.palette.error.main, 0.04) }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PersonRemoveIcon sx={{ fontSize: 20, color: "#EF4444" }} />
                      <Typography variant="subtitle2" fontWeight={700}>Unassigned Patients</Typography>
                      <Chip label={unassigned.length} size="small" sx={{ height: 22, fontWeight: 700, bgcolor: "#FEE2E2", color: "#991B1B" }} />
                    </Stack>
                  </Box>
                  <Box sx={{ height: 350 }}>
                    {loadingUnassigned ? (
                      <Stack spacing={1} sx={{ p: 2 }}>
                        {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
                      </Stack>
                    ) : (
                      <DataGrid
                        rows={unassigned}
                        columns={unassignedColumns}
                        getRowId={(row) => row.cipId}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25]}
                        disableSelectionOnClick
                        density="compact"
                        sx={{ border: "none", "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" } }}
                      />
                    )}
                  </Box>
                </Paper>

                {/* Assigned Patients (shown when PCP selected) */}
                {selectedPcp && (
                  <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid", borderColor: "divider", overflow: "hidden" }}>
                    <Box sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid", borderColor: "divider", bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <VisibilityIcon sx={{ fontSize: 20, color: "primary.main" }} />
                        <Typography variant="subtitle2" fontWeight={700}>
                          Patients of {selectedPcp.title} {selectedPcp.pcpName}
                        </Typography>
                        <Chip label={assignedPatients.length} size="small" sx={{ height: 22, fontWeight: 700, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1), color: "primary.main" }} />
                      </Stack>
                    </Box>
                    <Box sx={{ height: 400 }}>
                      {loadingAssigned ? (
                        <Stack spacing={1} sx={{ p: 2 }}>
                          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={40} />)}
                        </Stack>
                      ) : (
                        <DataGrid
                          rows={assignedPatients}
                          columns={assignedColumns}
                          getRowId={(row) => row.assignmentId || row.cipId}
                          pageSize={10}
                          rowsPerPageOptions={[10, 25, 50]}
                          disableSelectionOnClick
                          density="compact"
                          sx={{ border: "none", "& .MuiDataGrid-cell": { display: "flex", alignItems: "center" } }}
                        />
                      )}
                    </Box>
                  </Paper>
                )}
              </Stack>
            </Box>
          </Stack>
        )}
      </Stack>

      {/* ── Dialogs ──────────────────────────────────────────────── */}
      <AssignPcpDialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, patient: null })}
        patient={assignDialog.patient}
        pcps={pcps}
        onAssign={handleAssignPatient}
      />

      <TransferDialog
        open={transferDialog.open}
        onClose={() => setTransferDialog({ open: false, pcp: null })}
        sourcePcp={transferDialog.pcp}
        pcps={pcps}
        locationId={selectedLocationId}
        onTransfer={handleTransfer}
      />

      <PatientHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, cipId: null, name: "" })}
        cipId={historyDialog.cipId}
        patientName={historyDialog.name}
      />

      <PcpConfigDialog
        open={configDialog.open}
        onClose={() => setConfigDialog({ open: false, pcp: null })}
        pcp={configDialog.pcp}
        currentLocationId={selectedLocationId}
        onSaved={refreshAll}
      />

      {/* Auto-Assign Confirmation Dialog */}
      <Dialog open={autoAssignConfirm} onClose={() => !autoAssigning && setAutoAssignConfirm(false)} maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)", px: 3, py: 2.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <AutoFixHighIcon sx={{ color: "white" }} />
            <Typography variant="h6" fontWeight={700} sx={{ color: "white" }}>Auto-Assign</Typography>
          </Stack>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <Typography variant="body2">
            This will execute the automatic assignment process with 6 priority waves:
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 1.5, pl: 1 }}>
            {["Orphans cleanup", "High Risk", "High Cost", "Medicare", "Age Match", "Critical", "Remaining (equitable)"].map((w, i) => (
              <Typography key={i} variant="caption" color="text.secondary">
                {i === 0 ? "0." : `${i}.`} {w}
              </Typography>
            ))}
          </Stack>
          <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
            This operation cannot be undone immediately.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setAutoAssignConfirm(false)} disabled={autoAssigning} variant="outlined" sx={{ borderRadius: 2, textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            onClick={handleAutoAssign}
            disabled={autoAssigning}
            variant="contained"
            startIcon={autoAssigning ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)" }}
          >
            {autoAssigning ? "Running..." : "Execute"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Snackbar ─────────────────────────────────────────────── */}
      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
